import { token, type TokenName } from '../../tokens/tokens';
import chrome from './chartChrome.module.css';

/**
 * The one place a Figma token crosses into Recharts.
 *
 * Stylelint polices `src/**\/*.module.css`; it does not read `.tsx`. Anything
 * handed to Recharts as a *prop* is therefore outside the mechanical guard that
 * protects the rest of this system, so the crossings are funnelled through this
 * file and every one of them is looked up in the generated `token` map. A typo
 * fails `tsc` against `TokenName` instead of silently rendering black.
 *
 * As little as possible crosses. Recharts stamps its own presentation
 * attributes on tick text (`fill="#666"`), and a CSS class beats a presentation
 * attribute in the cascade — so all tick typography lives in
 * `chartChrome.module.css`, where stylelint can see it, and only the class name
 * comes through here.
 *
 * **The rule for what may be a `var()`:** colours only. `var()` resolves in SVG
 * paint attributes in every current engine, but Chromium does not resolve it in
 * *length* attributes (`stroke-width`, `x`, `r`). Lengths are therefore real
 * numbers — see `chartGeometry`.
 */

/**
 * Figma `_Legend` (421:47686) binds `chart/1` … `chart/15` to its fifteen
 * swatches in order, so series *n* takes `chart/(n+1)`. The ramp wraps past 15;
 * a chart with more than fifteen series repeats colours, which is a legibility
 * problem no palette solves — the caller should aggregate instead.
 */
export const chartSeriesTokens = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
  'chart-7',
  'chart-8',
  'chart-9',
  'chart-10',
  'chart-11',
  'chart-12',
  'chart-13',
  'chart-14',
  'chart-15',
] as const satisfies readonly TokenName[];

export type ChartSeriesToken = (typeof chartSeriesTokens)[number];

/** How many distinct colours the ramp offers before it repeats. */
export const chartPaletteSize = chartSeriesTokens.length;

/**
 * Resolve a series colour to a custom-property reference on the `chart/n` ramp.
 *
 * `override` is a `TokenName`, not a colour string, so a caller cannot smuggle a
 * literal hex into a chart — the type is the enforcement.
 */
export function seriesColor(index: number, override?: ChartSeriesToken): string {
  if (override) return token[override];
  return token[chartSeriesTokens[index % chartPaletteSize]];
}

/**
 * Lengths and counts Recharts needs as real numbers, because it uses them to
 * compute layout before anything reaches the DOM. Each mirrors a Figma value;
 * none can be a `var()`.
 *
 * This is the same exception the README already carves out for element
 * dimensions: Figma defines no width/height variables, so those are plain
 * numbers. A numeric mirror alongside `tokens.ts` would close the gap for the
 * three that *are* backed by tokens — see plan.md §7.
 */
export const chartGeometry = {
  /**
   * Figma `_Y-axis line` `Label=True`: a 40px right-aligned `Number` plus
   * `space/8` before the gridline starts. 40 + 8 = 48.
   */
  yAxisWidth: 48,
  /**
   * Figma `_X-axis`: the tick row is SPACE_BETWEEN with `space/24` at each end,
   * which is what keeps the first and last labels off the chart's edges.
   */
  xAxisPadding: 24,
  /**
   * Figma `_Y-axis` draws twelve `_Y-axis line` instances and leaves six
   * visible, so six is the designed gridline count.
   */
  gridLineCount: 6,
  /** Figma `_Y-axis line` `Divider` — `border-width/1`. */
  gridStrokeWidth: 1,
  /** Figma `_Y-axis line` `Divider` `dashPattern: [6, 6]`. */
  gridDashArray: '6 6',
  /**
   * Not in Figma, which draws the readout but not where it sits. `space/8`
   * between the hovered point and the tooltip — the gap `ChartTooltip` used to
   * put there with a CSS `translate`. Spent by `.hoverAnchor` — see
   * `useAnchoredTooltip`.
   */
  tooltipOffset: 8,
} as const;

/*
 * `tooltipPlacement` used to live here — Recharts' `offset`, plus a note on why
 * the readout went below-*right* of the point and let Recharts flip it. It is
 * gone (2026-09-24): Recharts no longer places the readout at all.
 *
 * `useAnchoredTooltip` does, because a readout that tracks the pointer cannot
 * hold a control the pointer is trying to reach. `chartGeometry.tooltipOffset`
 * survives as the gap, spent in `ChartFrame.module.css` against the anchor
 * instead of handed to Recharts, and the flipping the old comment described is
 * now `placementFor`'s three-way choice of side.
 */

/**
 * Figma `_Y-axis line` — a dashed horizontal rule in `chart/grid`. Figma draws
 * no vertical gridlines at all, so `horizontal` is the default and the only
 * orientation the file specifies.
 *
 * `lines` exists for a rotated plot, where the value axis runs horizontally and
 * the gridlines that mark it have to run vertically.
 * It is the same rule drawn against the same axis — only the chart is on its
 * side. Callers that pass nothing get exactly what they got before.
 */
export function gridProps(lines: 'horizontal' | 'vertical' = 'horizontal') {
  return {
    stroke: token['chart-grid'],
    strokeDasharray: chartGeometry.gridDashArray,
    strokeWidth: chartGeometry.gridStrokeWidth,
    vertical: lines === 'vertical',
    horizontal: lines === 'horizontal',
  } as const;
}

/**
 * How many labels a dense time axis aims for. Ten is legible at the `Chart`
 * slot's width and is the figure the product asked for.
 *
 * It is a target at the **586 and 1192 widths every module drew before
 * Overview**, and it is a ceiling rather than a constant: Overview (972:19366)
 * puts a 70-point daily series in a 384 card, where ten "Aug 26"-shaped labels
 * need about 400px of a ~320px axis and nine of the ten overlap their
 * neighbour — measured, on the page, before this was changed.
 *
 * Recharts can size this itself with `interval="equidistantPreserveStart"`, and
 * that was tried and rejected: it packs the axis to whatever fits, which drew
 * **24** labels on a 1192 card against the ten the product asked for. It
 * answers "how many fit" where the rule here is "how many should there be".
 * So the budget comes from the caller, which is the only thing that knows how
 * wide a column it put the chart in — see `CardSpan`.
 */
export const TARGET_TICKS = 10;

/**
 * At or below this many points every one is labelled. Twelve so that a year of
 * monthly buckets reads `Jan … Dec` in full rather than losing every other
 * month to the thinning rule.
 */
const LABEL_EVERY_POINT_UP_TO = 12;

/**
 * Figma `_X-axis` (421:47616) — labels only. The file draws no axis line and no
 * tick marks, so both are off; the row's visual weight is carried entirely by
 * the gridlines.
 *
 * `pointCount` is the number of rows being plotted. Pass it and the labels are
 * **evenly spaced**: a fixed numeric `interval` shows every nth tick, so the gap
 * between labels is a constant number of buckets.
 *
 * That replaces `preserveStartEnd` + `minTickGap`, which thinned by *pixel
 * width* and so spaced labels unevenly — "Sep 1 Sep 2 … Sep 9" then a gap, then
 * "Sep 11", because a two-digit day is wider than a one-digit one and tripped
 * the gap test at a different point along the axis. Figma's own `Data=30 days`
 * variant draws every second day, which is a fixed interval, not a width test.
 *
 * `minTickGap: 0` once an interval is set, or Recharts applies the width test
 * again on top of it and reintroduces the uneven gaps.
 *
 * `targetTicks` lowers the budget for a chart in a narrow column. It changes
 * how many labels there are, never how they are spaced — the interval stays a
 * fixed number of buckets, so the gaps stay even and the failure mode the
 * paragraph above describes cannot come back through this door.
 */
export function xAxisProps(dataKey: string, pointCount?: number, targetTicks = TARGET_TICKS) {
  const interval =
    pointCount === undefined
      ? ('preserveStartEnd' as const)
      : pointCount <= LABEL_EVERY_POINT_UP_TO
        ? 0
        : Math.ceil(pointCount / targetTicks) - 1;

  return {
    dataKey,
    axisLine: false as const,
    tickLine: false as const,
    tick: { className: chrome.tick },
    padding: { left: chartGeometry.xAxisPadding, right: chartGeometry.xAxisPadding },
    interval,
    minTickGap: pointCount === undefined ? 8 : 0,
  };
}

/**
 * Figma `_Y-axis` (421:47731) — a 48px gutter of right-aligned numbers, no axis
 * line, no tick marks, six gridlines.
 */
/**
 * `width` overrides Figma's 48px gutter, which is sized for the widest number
 * the file draws. `'auto'` sizes it to the labels this chart actually has —
 * worth passing wherever the numbers are short, because the gutter
 * right-aligns them and every unused pixel becomes dead space between the
 * card's edge and the first label. A +-10 scale labels "+10" at 20px wide and
 * so leaves 25px of it empty.
 */
export function yAxisProps(width: number | 'auto' = chartGeometry.yAxisWidth) {
  return {
    axisLine: false as const,
    tickLine: false as const,
    tick: { className: chrome.tick },
    width,
    tickCount: chartGeometry.gridLineCount,
  };
}

/* ------------------------------------------------------------------ series */

/**
 * One plotted series. `color` is a `TokenName` rather than a colour string, so
 * the type is what stops a literal hex reaching a chart; omit it and the series
 * takes its position on the ramp.
 */
export type ChartSeries = {
  /** Key to read from each data row. */
  key: string;
  /** Shown in the legend and the tooltip. */
  label: string;
  /** Override this series' position on the `chart/n` ramp. */
  color?: ChartSeriesToken;
};

/**
 * Where the legend sits relative to the plot. `none` is for composing into a
 * `Card`, which gives the legend its own slot — see `legendItems()`.
 */
export type ChartLegendPlacement = 'bottom' | 'right' | 'none';

/**
 * Resolve a series list to legend rows. Exported so a caller placing the legend
 * in `Card`'s own `Legend` slot gets the same colours the chart used, rather
 * than re-deriving the palette and drifting from it.
 */
export function legendItems(series: ChartSeries[]): { label: string; color: string }[] {
  return series.map((s, i) => ({ label: s.label, color: seriesColor(i, s.color) }));
}

/**
 * Recharts' hover cursor. Not in Figma — the file draws no hover state at all —
 * so it borrows the gridline's own colour, which keeps the strongest thing on
 * the plot the data itself.
 */
export function tooltipCursor(kind: 'line' | 'bar') {
  return kind === 'line'
    ? { stroke: token['chart-grid'], strokeWidth: chartGeometry.gridStrokeWidth }
    : { fill: token['surface-background'] };
}

/**
 * A redraw — mount, or a data update — eases marks into position using
 * `motion.css`'s own vocabulary. (2026-09-16, reversing the note this replaces:
 * Recharts' transitions used to read neither the duration tokens nor
 * `prefers-reduced-motion`, which stopped being true once `isAnimationActive`
 * gained an `'auto'` mode — see below.)
 *
 * **Duration and easing are picked by category, not by chart type**, the same
 * way `motion.css` grades every other duration: a chart redraw is marks moving
 * from one on-screen position to another, which is `--duration-base` and
 * `--ease-in-out` by the token file's own definitions ("layout choreography,
 * where the eye has to follow something moving" / "something moving between
 * two on-screen positions") — not `--ease-out`, which is for something
 * *arriving*, and not `--duration-fast`, which is sized for a hover or a press
 * rather than a redraw a user watches settle.
 *
 * **Mirrored as real numbers, not `var()`.** Recharts' `react-smooth` consumes
 * `animationDuration` and `animationEasing` as plain JS values before anything
 * reaches the DOM — same category as `chartGeometry` above, and the same reason
 * a CSS custom property cannot cross here. `animationEasing` still carries the
 * token's own cubic-bezier *literal* rather than a resolved number, because
 * that literal is Recharts' own accepted string syntax (`NamedBezier` in its
 * types) — it is copied verbatim from `--ease-in-out`'s value in `motion.css`,
 * not derived from it, so the two have to be changed together if either moves.
 *
 * **`isAnimationActive: 'auto'` is what makes `prefers-reduced-motion` free.**
 * Recharts' own hook (`usePrefersReducedMotion`) subscribes to the media query
 * and disables animation live when it matches — the same guarantee every other
 * component here gets from routing through `motion.css`'s tokens, just enforced
 * in Recharts' code instead of ours because a duration prop cannot itself
 * collapse under a media query the way a CSS custom property can.
 *
 * **The parity harness stays deterministic in practice, not by construction.**
 * A screenshot taken mid-tween would still be a different chart, and nothing
 * here prevents that — `check-parity.mjs`'s default 5000ms settle wait is what
 * keeps every measurement past this animation's 180ms, comfortably. A future
 * `--settle` lower than that would need to either wait for animation to finish
 * or disable it, neither of which exists yet.
 */
export const chartAnimation = {
  isAnimationActive: 'auto',
  /* --duration-base — see the note above for why 180 is written here, not var(). */
  animationDuration: 180,
  /* --ease-in-out's own value, copied verbatim — Recharts' accepted literal syntax. */
  animationEasing: 'cubic-bezier(0.4,0,0.2,1)',
} as const;

/* ------------------------------------------- trend, rank and share charts */

/**
 * Geometry for the marks the trend, ranked-bar and share charts draw.
 *
 * Same rule as `chartGeometry` above: Recharts reads these while it computes
 * layout, before anything reaches the DOM, so every one is a real number and
 * none can be a `var()`.
 *
 * **None of it is in Figma.** The file designs the cartesian chrome and stops —
 * no bar width, no line weight, no dash pattern for a series, no area fill, no
 * annotation. See plan.md §7 items 132, 135 and 136. Each value below is
 * derived and says from what, so a designer can overrule it with a number
 * rather than a guess.
 */
export const chartMarkGeometry = {
  /**
   * Line weight. The same 2px `LineChart` draws, so a trend and a line chart in
   * the same view never read as two different systems.
   */
  lineStrokeWidth: 2,
  /**
   * A comparison series — last period against this one.
   *
   * `_Y-axis line` already owns `[6, 6]`, so a dashed *series* has to be
   * distinguishable from a gridline at a glance: this runs tighter, 4 on and 3
   * off, and sits on the data's own colour rather than `chart/grid`.
   */
  comparisonDashArray: '4 3',
  /** A rolling average — looser than a comparison line, so the two can coexist. */
  averageDashArray: '5 4',
  /**
   * A de-emphasised series.
   *
   * Opacity, not a second colour: `chart/1` … `chart/15` is fifteen hues with no
   * tints, so a lighter variant would be a colour the file does not define.
   */
  mutedOpacity: 0.4,
  /** Recharts' hover dot, matching `LineChart`'s. */
  activeDotRadius: 4,
  /**
   * An area's fill under its own stroke. Low enough that three stacked bands
   * stay distinct where they meet, which is the whole job of a share chart.
   */
  areaFillOpacity: 0.16,

  /**
   * A scatter point's area in px², which is what Recharts' `ZAxis` `range`
   * measures — not its radius. `[min, max]` because a bubble chart's whole
   * argument is that the *size* carries a second reading.
   *
   * The floor is 56 (r ≈ 4.2), a shade above `activeDotRadius` so the smallest
   * bubble still reads as a mark rather than as a stray hover dot. The ceiling
   * is 520 (r ≈ 12.9), which at the `Chart` slot's 224px height is about a
   * seventeenth of the plot — large enough to dominate, small enough that two
   * peaks on adjacent buckets do not merge into one blob.
   */
  scatterSizeRange: [56, 520] as [number, number],
  /**
   * A uniform point's area, for a scatter whose size carries nothing. Sits at
   * the geometric middle of the range above so a sized chart and an unsized one
   * read as the same component.
   */
  scatterPointArea: 170,
  /**
   * Bubbles overlap by nature — two brands can peak on the same day — so the
   * fill is translucent and an overlap reads as an overlap rather than as one
   * mark hiding another. A stroke would be the alternative and is worse here:
   * at these radii a 1px ring is a third of the smallest bubble's width.
   */
  scatterFillOpacity: 0.72,
} as const;

/**
 * A plotted series that can be drawn as a secondary reading.
 *
 * Extends `ChartSeries` rather than changing it: `LineChart` and `BarChart` take
 * the base type and are untouched by this: nothing they already render moves.
 */
export type TrendSeries = ChartSeries & {
  /**
   * Draw the line dashed. `comparison` is the prior period, `average` a rolling
   * mean — two patterns so a chart can carry both without them colliding.
   */
  dash?: 'comparison' | 'average';
  /**
   * Drop the line back so another series reads as the subject. The colour is
   * unchanged, so the legend and the tooltip still match.
   */
  muted?: boolean;
};

/**
 * Everything a `<Line>` needs to draw one `TrendSeries`, resolved from the ramp.
 *
 * Returned as props rather than applied here so the chart stays the thing that
 * renders and this stays the thing that decides — the same split `xAxisProps`
 * and `yAxisProps` already use.
 */
export function trendLineProps(series: TrendSeries, index: number) {
  const dashArray =
    series.dash === 'comparison'
      ? chartMarkGeometry.comparisonDashArray
      : series.dash === 'average'
        ? chartMarkGeometry.averageDashArray
        : undefined;

  return {
    stroke: seriesColor(index, series.color),
    strokeWidth: chartMarkGeometry.lineStrokeWidth,
    strokeDasharray: dashArray,
    strokeOpacity: series.muted ? chartMarkGeometry.mutedOpacity : 1,
  };
}

/**
 * Class names for SVG text Recharts renders but CSS can still own.
 *
 * Recharts stamps `fill="#666"` and a font size on text it draws; a class beats
 * a presentation attribute outright, which is what keeps this typography in a
 * stylesheet where stylelint can prove there is no hardcoded value in it. Only
 * the name crosses — the same arrangement `xAxisProps` uses for ticks.
 */
export const chartTextClass = {
  /** Axis ticks. */
  tick: chrome.tick,
  /** A label at the end of a line, standing in for a legend entry. */
  endLabel: chrome.endLabel,
  /** An annotation's caption. */
  annotation: chrome.annotation,
  /** A value printed beside its own bar. */
  valueLabel: chrome.valueLabel,
  /** The caption on a reference line — what the dashed rule is. */
  referenceLabel: chrome.referenceLabel,
} as const;

/* ------------------------------------------------ bars, rotation, reference */

/**
 * Geometry for bars and for a rotated (horizontal) plot.
 *
 * Same rule as `chartGeometry`: Recharts reads these while it computes layout,
 * so every one is a real number and none can be a `var()`.
 *
 * **Figma specifies none of it.** The `Charts` page draws cartesian chrome and
 * stops — no bar width, no corner radius, no rotated variant. Each value below
 * therefore says what it was derived from, so a designer can overrule it with a
 * number rather than a guess. See plan.md §7 item 132.
 */
export const chartBarGeometry = {
  /**
   * A bar's corner radius, on the value end only — the end that floats. Mirrors
   * `radius/xxs`, the radius `Progress Bar` gives its own 4x16 segments, which
   * is the one other place this system draws a small rectangle. Square corners
   * were the previous assumption; 1px is the same assumption with the system's
   * own answer for "how round is a small rectangle" applied to it.
   */
  radius: 1,
  /** Gap between bars inside one category. `space/2`. */
  barGap: 2,
  /**
   * Gap between categories, as a share of the band. **Not derivable** — the
   * spacing scale is absolute and Recharts wants a percentage here, so this is
   * the one bar value that is a straight invention. 20% keeps neighbouring
   * groups legibly apart without thinning the bars into rules.
   */
  categoryGap: '20%',
  /**
   * The gutter a rotated plot reserves for its category labels. Mirrors
   * `space/128`, which is the widest step on the spacing scale and comfortably
   * holds the longest label the corpus produces ("Data pack pricing").
   *
   * The numeric-axis equivalent is `chartGeometry.yAxisWidth`, 48, which is
   * sized for numbers and far too narrow for words.
   */
  categoryAxisWidth: 128,
  /**
   * The thickest a bar or column is drawn — its default *and* its ceiling, in
   * both orientations.
   *
   * **A cap, not a pinned size**, and the distinction is the whole design. A bar
   * takes 12 wherever its band has room, and less where it does not, which is
   * what keeps a dense chart from becoming a solid block: fourteen intents in the
   * 224 slot is a 16px band, and at a pinned 12 the bars sat 2px apart and read
   * as one filled rectangle rather than fourteen measurements. Capped, that
   * chart draws at 8 and the rows separate again. Brand Health's `Engagement
   * volume over time` is the same case on the other axis — ~70 daily buckets
   * across a 538px plot, a 7.7px band, drawing at 4.
   *
   * It was 24 until 2026-09-17, which was **above** the natural width of every
   * rotated chart in the app, so on that axis the cap never bit at all and the
   * bar was simply a share of its band. 12 is low enough to bind on everything
   * up to about eight categories, which is where the charts that want a
   * deliberate weight actually live.
   *
   * The consequence, accepted knowingly: past eight categories the thickness
   * follows the row count again (14 at nine, 8 at fourteen). That is the honest
   * behaviour for a chart running out of room — the alternative is bars that
   * touch. A chart that needs both many rows and a deliberate weight wants a
   * `limit` on its pivot, not a thinner bar.
   */
  maxBarSize: 12,
} as const;

/**
 * Props for the category axis of a **rotated** plot — horizontal bars, where
 * the categories run down the left-hand side.
 *
 * **The `tickSize`/`tickMargin` pair is load-bearing, not tidiness.** Recharts
 * positions tick text at `tx = x + width - tickSize - tickMargin`. Leaving the
 * defaults (6 and 2) with `textAnchor: 'start'` starts every label 8px left of
 * the gutter, so the first character is clipped off-canvas. Zeroing both and
 * offsetting by `-width` anchors the label to the left edge of the space the
 * axis reserved, which is where a category label belongs.
 *
 * `interval: 0` because every category must be labelled — a bar with no name is
 * not a chart. That is the opposite of `xAxisProps`, which thins a dense time
 * axis on purpose.
 */
export function categoryAxisProps(
  dataKey: string,
  width: number = chartBarGeometry.categoryAxisWidth,
) {
  return {
    type: 'category' as const,
    dataKey,
    axisLine: false as const,
    tickLine: false as const,
    tickSize: 0,
    tickMargin: 0,
    width,
    interval: 0 as const,
    tick: { className: chrome.tick, textAnchor: 'start' as const, dx: -width },
  };
}

/**
 * Props for the value axis of a rotated plot — the numeric axis, running along
 * the bottom. The mirror image of `yAxisProps`.
 *
 * **`space/24` of trailing padding, since 2026-09-18.** The axis along the bottom
 * of a rotated plot is still Figma's `_X-axis`, whose row carries `space/24` at
 * each end — the same padding `xAxisProps` has always given a vertical chart.
 * This preset had none, so Recharts centred the last tick on the plot's right
 * edge and the SVG cut off the half that overhung: measured on every horizontal
 * bar chart in the app, "100%" lost 1.9px and "45.0%" 3px, and the Competitors
 * breakdown drawer's "1,500" rendered as "1,50C". Trailing only: padding the
 * leading end would lift the bars off their zero baseline at the category
 * gutter, which is not what the file draws.
 */
export function valueAxisProps() {
  return {
    type: 'number' as const,
    axisLine: false as const,
    tickLine: false as const,
    tick: { className: chrome.tick },
    tickCount: chartGeometry.gridLineCount,
    padding: { left: 0, right: chartGeometry.xAxisPadding },
  };
}

/**
 * A reference mark — a threshold, target, benchmark or prior-period baseline.
 *
 * One treatment for all of them, on `chart/reference`, so a reference is never
 * mistaken for a data series. The colour is a real token as of 2026-09; before
 * that the only candidate was `border/info`, the same blue under a name that
 * meant something else.
 *
 * Dashed with `averageDashArray` rather than a pattern of its own: `[6, 6]`
 * already belongs to the gridline and `[4, 3]` to a comparison series, and a
 * fourth pattern would be a distinction no reader could name.
 *
 * `ifOverflow: 'extendDomain'` so a threshold above every data point still
 * appears — silently clipping the line would make a chart claim the target was
 * met.
 */
export function referenceLineProps() {
  return {
    stroke: token['chart-reference'],
    strokeDasharray: chartMarkGeometry.averageDashArray,
    strokeWidth: chartGeometry.gridStrokeWidth,
    ifOverflow: 'extendDomain' as const,
  };
}

/* ------------------------------------------------------------- diverging */

/**
 * The spine of a diverging plot — the zero line a population pyramid is read
 * against.
 *
 * Solid and on `border/primary`, deliberately **not** `referenceLineProps`.
 * That treatment is one dashed blue rule for every *comparison* mark — a
 * threshold, a target, a benchmark, a prior period — and the whole point of it
 * is that a reference is never mistaken for a plotted series. A zero line is
 * neither: it is the axis the chart is drawn around, structural in the same way
 * a gridline is, and giving it the comparison treatment would claim the origin
 * was a target somebody set.
 */
export function axisBaselineProps() {
  return { stroke: token['border-primary'], strokeWidth: chartGeometry.gridStrokeWidth };
}

/** Steps a diverging axis takes on each side of zero. Six intervals in all. */
const DIVERGING_STEPS_PER_SIDE = 3;

/** The smallest `1`, `2` or `5` times a power of ten that reaches `value`. */
function niceStep(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5]) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return 10 * magnitude;
}

/**
 * A symmetric domain and its ticks, for a plot that runs both ways from zero.
 *
 * **Both are computed, and neither can be left to Recharts.** A pyramid whose
 * two sides are scaled differently is not a pyramid — the comparison *is* the
 * chart — so the domain has to be symmetric rather than fitted to the data.
 * And a pinned domain needs pinned ticks: on `[-10, 10]` at six "nice"
 * gridlines Recharts has been measured choosing a step of 8 and labelling
 * `-6, +2, +10`, skipping both zero and the bound, which are the only two
 * numbers a signed scale is actually read by.
 *
 * Three steps a side rather than a round bound, because a round bound has to be
 * reached by rounding *up* and leaves the plot sitting in the bottom half of its
 * own scale: 25 rounds to 40 and the longest bar then fills five eighths of the
 * space. Sizing the step instead gives 25 a bound of 30.
 *
 * The step floors at 1 when every value is a whole number, so a chart of counts
 * cannot draw a gridline at 2.5 and label it `3`.
 */
export function divergingScale(values: readonly number[]): {
  domain: [number, number];
  ticks: number[];
} {
  const live = values.filter((value) => Number.isFinite(value)).map(Math.abs);
  const max = live.length > 0 ? Math.max(...live) : 0;

  const integral = live.every(Number.isInteger);
  const step = Math.max(
    niceStep(max / DIVERGING_STEPS_PER_SIDE),
    integral ? 1 : Number.NEGATIVE_INFINITY,
  );
  const bound = step * DIVERGING_STEPS_PER_SIDE;

  const ticks: number[] = [];
  for (let i = -DIVERGING_STEPS_PER_SIDE; i <= DIVERGING_STEPS_PER_SIDE; i += 1) {
    ticks.push(i * step);
  }

  return { domain: [-bound, bound], ticks };
}

/**
 * A zero-based domain and its ticks, for a value axis whose automatic ones land
 * badly.
 *
 * The sibling of `divergingScale`, sharing its step logic. Recharts fits an
 * automatic domain to the data's own maximum and then divides it by the tick
 * count, so a chart whose largest bar is 4.7 draws gridlines at
 * `0.0, 0.9, 1.9, 2.9, 3.8, 4.8` — six labels, not one of them a number anybody
 * would choose, and every one of them carrying a decimal the reading does not
 * need. Sizing a round step and running the axis past the data instead gives
 * `0, 1, 2, 3, 4, 5`.
 *
 * `steps` is the number of intervals, so the tick count is one more.
 */
export function linearScale(
  values: readonly number[],
  steps: number = chartGeometry.gridLineCount - 1,
): { domain: [number, number]; ticks: number[] } {
  const live = values.filter((value) => Number.isFinite(value) && value >= 0);
  const max = live.length > 0 ? Math.max(...live) : 0;

  const integral = live.every(Number.isInteger);
  const step = Math.max(niceStep(max / steps), integral ? 1 : Number.NEGATIVE_INFINITY);
  const bound = step * steps;

  return {
    domain: [0, bound],
    ticks: Array.from({ length: steps + 1 }, (_, i) => i * step),
  };
}
